// @ts-check
/**
 * @typedef {{id?: unknown, name?: unknown, email?: unknown}} DashboardAccountProfile
 * @typedef {{status?: string, profile?: DashboardAccountProfile | null}} DashboardAccountState
 * @typedef {{ready: () => unknown, getState: () => DashboardAccountState | null | undefined,
 *   logout: () => unknown}} DashboardAccountSession
 * @typedef {{state: Element | null, feedback: Element | null, logout: HTMLButtonElement | null,
 *   profile: HTMLElement | null, name: Element | null, email: Element | null,
 *   fieldNote: HTMLElement | null}} DashboardAccountNodes
 * @typedef {Window & typeof globalThis & {
 *   LittleGWebSession?: DashboardAccountSession,
 *   __GCP_DASHBOARD_ACCOUNT_PRESENTATION__?: {refresh: () => void}
 * }} DashboardAccountWindow
 */
/* assets/gcitypartners-dashboard-account.js — GCITYPARTNERS-DASHBOARD-ACCOUNT-01
 * Presentation consumer of LittleGWebSession for the GCityPartners dashboard.
 * Owns no credentials, role, Provider approval or protected authorization.
 */
(function (/** @type {DashboardAccountWindow} */ window, document) {
  'use strict';

  const ACCOUNT_RUNTIME_KEY = '__GCP_DASHBOARD_ACCOUNT_PRESENTATION__';

  if (window[ACCOUNT_RUNTIME_KEY]) {
    window[ACCOUNT_RUNTIME_KEY].refresh();
    return;
  }

  var ROOT = '[data-gcitypartners-dashboard]';

  var SIGN_IN = '/pages/gcitypartners-sign-in';
  /** @type {WeakSet<Element>} */
  var mounted = new WeakSet();
  /** @type {WeakMap<Element, {status: string, subject: string}>} */
  var sessionPresentation = new WeakMap();
  /** @type {number | null} */
  let heroTimer = null;

  /**
   * @param {unknown} value
   */
  function clean(value) { return String(value == null ? '' : value).trim(); }
  function isZh() { return /^zh/i.test(clean(document.documentElement.lang)); }
  /**
   * @param {string} zh
   * @param {string} en
   * @param {string} [hans]
   */
  function words(zh, en, hans) {
    const language = clean(document.documentElement.lang).toLowerCase();
    return /^zh-(cn|hans|sg)(?:-|$)/.test(language) ? (hans || zh) : (isZh() ? zh : en);
  }
  function heroClock() {
    const now = new Date();
    const language = clean(document.documentElement.lang).toLowerCase();
    const locale = /^zh-(cn|hans|sg)(?:-|$)/.test(language) ? 'zh-CN' : (isZh() ? 'zh-HK' : 'en-GB');
    const timeZone = 'Asia/Hong_Kong';

    try {
      const hourParts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone,
        hour: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(now);

      const hourPart = hourParts.find((part) => part.type === 'hour');

      return {
        hour: hourPart ? Number(hourPart.value) : null,
        date: new Intl.DateTimeFormat(locale, {
          timeZone: timeZone,
          month: 'long',
          day: 'numeric',
          weekday: 'short'
        }).format(now)
      };
    } catch (_) {
      return { hour: null, date: '' };
    }
  }

  function owner() {
    var session = window.LittleGWebSession;
    return session
      && typeof session.ready === 'function'
      && typeof session.getState === 'function'
      && typeof session.logout === 'function'
      ? session
      : null;
  }

  /**
   * @param {DashboardAccountState | null | undefined} current
   * @param {string} status
   */
  function heroName(current, status) {
    if (status !== 'authenticated') return '';

    const value = current && current.profile && current.profile.name;
    if (typeof value !== 'string') return '';

    const name = value
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!name || name.includes('@')) return '';
    return Array.from(name).slice(0, 40).join('');
  }

  /**
   * @param {Element} root
   */
  function nodes(root) {
    return /** @type {DashboardAccountNodes} */ ({
      state: root.querySelector('[data-gcp-account-state]'),
      feedback: root.querySelector('[data-gcp-account-feedback]'),
      logout: /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-gcp-account-logout]')),
      profile: /** @type {HTMLElement | null} */ (root.querySelector('[data-gcp-product-account]')),
      name: root.querySelector('[data-gcp-account-name]'),
      email: root.querySelector('[data-gcp-account-email]'),
      fieldNote: root.querySelector('[data-gcp-account-field-note]')
    });
  }

  /**
   * @param {ReturnType<typeof nodes>} view
   * @param {DashboardAccountState | null | undefined} current
   * @param {string} status
   */
  function renderAccountProfile(view, current, status) {
    const profile = status === 'authenticated'
      && current
      && current.profile
      && typeof current.profile === 'object'
      && !Array.isArray(current.profile)
      && clean(current.profile.id)
      ? current.profile
      : null;

    const name = profile ? heroName(current, status) : '';
    const email = profile && typeof profile.email === 'string'
      ? profile.email
        .replace(/[\u0000-\u001f\u007f]/g, '')
        .trim()
        .slice(0, 254)
      : '';

    if (view.name) {
      view.name.textContent = profile
        ? name || words('未提供', 'Not provided')
        : '';
    }

    if (view.email) {
      view.email.textContent = profile
        ? email || words('未提供', 'Not provided')
        : '';
    }

    if (view.profile) {
      view.profile.hidden = !profile;
    }
    // The signed native session may carry only a subject. Do not invent a
    // name, reuse a Shopify account email, or infer one from cached state.
    if (view.fieldNote) {
      view.fieldNote.hidden = !profile || Boolean(name && email);
    }
  }

  /**
   * @param {Element} root
   * @param {Element | null} stateNode
   */
  function ensureSignIn(root, stateNode) {
    if (!stateNode || root.querySelector('[data-gcp-account-sign-in]')) return;
    var link = document.createElement('a');
    link.href = SIGN_IN;
    link.className = 'gcp-dashboard__button';
    link.setAttribute('data-gcp-account-sign-in', '');
    link.textContent = words('登入 GCityPartners', 'Sign in to GCityPartners', '登录 GCityPartners');
    stateNode.insertAdjacentElement('afterend', link);
  }

  /**
   * @param {Element} root
   */
  function removeSignIn(root) {
    var link = root.querySelector('[data-gcp-account-sign-in]');
    if (link) link.remove();
  }
  /**
   * @param {Element} root
   * @param {DashboardAccountState | null | undefined} current
   * @param {string} status
   */
  function renderHero(root, current, status) {
    const welcome = root.querySelector('.gcp-dashboard__hero-copy');
    const title = welcome && welcome.firstElementChild;
    const lead = root.querySelector('.gcp-dashboard__lead');

    if (!title || title.tagName !== 'H1') return;

    const clock = heroClock();
    const name = heroName(current, status);
    let greeting = words('你好', 'Hello');

    if (typeof clock.hour === 'number' && Number.isFinite(clock.hour)) {
      if (clock.hour < 12) {
        greeting = words('早上好', 'Good morning');
      } else if (clock.hour < 18) {
        greeting = words('下午好', 'Good afternoon');
      } else {
        greeting = words('晚上好', 'Good evening');
      }
    }

    const heading = status === 'authenticated'
      ? greeting + (name ? (isZh() ? '，' : ', ') + name : '')
      : words('GCityPartners 工作台', 'GCityPartners workspace');

    if (title.textContent !== heading) {
      title.textContent = heading;
    }

    if (lead) {
      const summary = words(
        '查看項目進展，整理下一步需求，連接合適的專業支援。',
        'Review your progress, prepare your next brief and connect with professional support.'
      );
      const description = [clock.date, summary]
        .filter(Boolean)
        .join(' · ');

      if (lead.textContent !== description) {
        lead.textContent = description;
      }
    }
  }


  /**
   * @param {Element} root
   */
  function render(root) {
    const session = owner();
    var view = nodes(root);
    var current = session && session.getState();
    var status = clean(current && current.status) || 'unavailable';
    const previous = sessionPresentation.get(root);
    const subject = status === 'authenticated'
      ? clean(current && current.profile && current.profile.id)
      : '';
    const identityChanged = previous && previous.status === 'authenticated'
      && (status !== 'authenticated' || previous.subject !== subject);
    // Reset read-only private projections whenever the native session becomes
    // unresolved or its identity changes. Never rely on CSS as data cleanup.
    if (root.hasAttribute('data-gcp-guest-gate')
      && (status !== 'authenticated' || identityChanged)) {
      root.querySelectorAll('[data-gcp-overview-module]').forEach((module) => {
        module.dispatchEvent(new CustomEvent('gcp:overview-reset', {
          detail: { state: 'unauthorized' }
        }));
      });
    }
    sessionPresentation.set(root, { status, subject });
    root.setAttribute('data-gcp-native-session-state', status);
    if (root.hasAttribute('data-gcp-guest-gate')) {
      const heading = status === 'authenticated'
        ? root.querySelector('.gcp-dashboard__hero-copy h1')
        : root.querySelector('[data-gcp-guest-preview] h1');
      if (heading && heading.id) root.setAttribute('aria-labelledby', heading.id);
    }
    // This status is presentation-only. It never grants project or Provider access.
    const guestStatus = root.querySelector('[data-gcp-guest-status]');
    if (guestStatus) {
      guestStatus.textContent = status === 'booting' || status === 'authenticating'
        ? words('正在確認登入狀態…', 'Checking your sign-in status…')
        : status === 'guest' || status === 'anonymous'
          ? words('公開服務可自由探索；只有私人工作需要登入。', 'Explore public services freely; sign in only for private work.')
          : status === 'authenticated'
            ? ''
            : words('目前無法核實登入狀態。你仍可探索公開服務。', 'Sign-in cannot be verified. Public services are still available.');
    }

    renderAccountProfile(view, current, status);
    renderHero(root, current, status);

    if (view.logout) {
      view.logout.hidden = status !== 'authenticated';
    }

    if (status === 'authenticated') {
      removeSignIn(root);
      if (view.state) view.state.textContent = words('已登入 GCityPartners', 'Signed in to GCityPartners', '已登录 GCityPartners');
      if (view.logout) view.logout.disabled = false;
      if (view.feedback) view.feedback.textContent = '';
      return;
    }

    if (status === 'booting' || status === 'authenticating') {
      removeSignIn(root);
      if (view.state) view.state.textContent = words('正在確認登入狀態…', 'Checking sign-in status…', '正在确认登录状态…');
      if (view.logout) view.logout.disabled = true;
      return;
    }

    if (status === 'guest' || status === 'anonymous') {
      if (view.state) view.state.textContent = words('尚未登入 GCityPartners', 'Not signed in to GCityPartners', '尚未登录 GCityPartners');
      if (view.logout) view.logout.disabled = true;
      ensureSignIn(root, view.state);
      return;
    }

    removeSignIn(root);
    if (view.state) view.state.textContent = words('暫時無法確認登入狀態', 'Sign-in status is temporarily unavailable', '暂时无法确认登录状态');
    if (view.logout) view.logout.disabled = true;
  }

  /**
   * @param {Element} root
   */
  async function resolve(root) {
    var session = owner();
    if (!session) {
      root.setAttribute('data-gcp-native-session-state', 'unavailable');
      render(root);
      return;
    }
    render(root);
    try { await session.ready(); } catch (_) {}
    if (root.isConnected) render(root);
  }

  /**
   * @param {Element} root
   */
  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    var view = nodes(root);
    if (view.logout) {
      view.logout.addEventListener('click', function () {
        var session = owner();
        if (!session || !view.logout || view.logout.disabled) return;
        view.logout.disabled = true;
        renderAccountProfile(view, null, 'guest');
        if (view.feedback) view.feedback.textContent = words('正在登出…', 'Signing out…', '正在退出登录…');
        Promise.resolve(session.logout())
          .then(function () {
            if (!root.isConnected) return;
            render(root);
            if (view.feedback) view.feedback.textContent = words('已登出。', 'Signed out.', '已退出登录。');
          })
          .catch(function () {
            if (!root.isConnected) return;
            render(root);
            if (view.feedback) view.feedback.textContent = words('暫時無法完成登出。', 'Sign-out could not be completed.', '暂时无法退出登录。');
          });
      });
    }
    void resolve(root);
  }

  function refreshHero() {
    const session = owner();
    const current = session && session.getState();
    const status = clean(current && current.status) || 'unavailable';

    document.querySelectorAll(ROOT).forEach((root) => {
      renderHero(root, current, status);
    });
  }

  function refresh() {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT), function (/** @type {Element} */ root) {
      if (mounted.has(root)) render(root); else mount(root);
    });
  }

  function stopHeroTimer() {
    if (heroTimer === null) return;
    window.clearInterval(heroTimer);
    heroTimer = null;
  }

  function startHeroTimer() {
    if (
      heroTimer !== null ||
      document.visibilityState === 'hidden' ||
      !document.querySelector(ROOT)
    ) return;

    heroTimer = window.setInterval(function () {
      if (!document.querySelector(ROOT)) {
        stopHeroTimer();
        return;
      }
      if (document.visibilityState !== 'hidden') refreshHero();
    }, 60000);
  }

  window[ACCOUNT_RUNTIME_KEY] = Object.freeze({
    refresh: function () {
      refresh();
      startHeroTimer();
    }
  });

  document.addEventListener('littleg:web-session-change', refresh);

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      stopHeroTimer();
      return;
    }
    refreshHero();
    startHeroTimer();
  });

  window.addEventListener('pagehide', stopHeroTimer);

  window.addEventListener('pageshow', function () {
    refreshHero();
    startHeroTimer();
  });

  document.addEventListener('shopify:section:load', function () {
    refresh();
    startHeroTimer();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const target = event.target;
    if (!target || !('contains' in target) || typeof target.contains !== 'function') return;

    const container = /** @type {EventTarget & Pick<Element, 'contains'>} */ (target);
    const remaining = Array.from(document.querySelectorAll(ROOT))
      .some((root) => !container.contains(root));

    if (!remaining) stopHeroTimer();
  });

  function init() {
    refresh();
    startHeroTimer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})(/** @type {DashboardAccountWindow} */ (window), document);
